import React from "react";
import { Link } from "react-router-dom";
import Logo from "./Logo";
import { Mail } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { CATEGORIES } from "../mock";

/**
 * Live social profiles. Empty until the accounts exist — see the note by the
 * render below. Shape: { Icon, href, label }, with Icon imported from lucide.
 */
const SOCIAL = [];

export default function Footer() {
  const { t } = useLang();

  const cols = [
    {
      title: "Explore",
      links: [
        { label: t("nav_browse"), to: "/browse" },
        { label: t("section_bestsellers"), to: "/browse?sort=rating" },
        { label: t("section_new"), to: "/browse?sort=newest" },
      ],
    },
    {
      title: t("section_categories"),
      links: CATEGORIES.slice(0, 4).map((c) => ({ label: c.en, to: `/browse?cat=${c.id}` })),
    },
    {
      title: "Your account",
      links: [
        { label: t("nav_library"), to: "/library" },
        { label: t("wishlist"), to: "/wishlist" },
        { label: t("redeem"), to: "/redeem" },
      ],
    },
  ];

  return (
    <footer className="border-t border-border bg-card/40 mt-20">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
          <div className="col-span-2 md:col-span-1">
            <Logo size="sm" className="mb-4" />
            <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">{t("footer_tag")}</p>
            {/* Only accounts that exist are linked. Four icons that swallowed
                the click read as a broken site, which costs more trust than an
                absent social row. Add an entry here when a profile is live. */}
            {SOCIAL.length > 0 && (
              <div className="flex gap-3 mt-5">
                {SOCIAL.map(({ Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label={label}
                    className="w-9 h-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-brand-400 hover:border-brand-500/50 transition-colors"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            )}
            <a
              href="mailto:hello@trellure.com"
              className="inline-flex items-center gap-2 mt-5 text-sm text-muted-foreground hover:text-brand-400 transition-colors"
            >
              <Mail className="w-4 h-4" /> hello@trellure.com
            </a>
          </div>
          {cols.map((col) => (
            <div key={col.title}>
              <h4 className="text-sm font-semibold mb-4">{col.title}</h4>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-12 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} Trellure. {"All rights reserved."}</p>
        </div>
      </div>
    </footer>
  );
}
